import compact from 'lodash/compact';
import { SxProps } from 'src/theme/types';
import Select from 'src/components/Select/Select';
import Switch from 'src/components/Switch';
import TextField from '@mui/material/TextField';
import MaskedInput from 'src/components/MaskedInput';
import * as Yup from 'yup';
import AutoCompleteWithFreeText from 'src/components/AutoComplete/AutoCompleteWithFreeText';
import { DEVICE_GROUP_FILTER_CONDITION_OPTIONS } from '../SolutionDevices/constants';
import {
  RULE_NOTIFICATION_PREFERENCE_OPTIONS,
  RULE_TIME_UNITS,
} from './constant';
import {
  SolutionRuleConfigurationField,
  SolutionRuleConfigurationFilterField,
} from '../types/solutionRulesConfiguration';
import {
  SUBSCRIBE_FILTER_OPERATOR_OPTIONS,
  SUBSCRIPTION_SAS_PATTERN,
} from '../SolutionSubscriptions/constants';
import SolutionRuleConfigurationEditFormRow from './components/SolutionRuleConfigurationEditFormRow';

const inputStyle: SxProps = { width: ({ spacing }) => spacing(40) };

export const getRuleConfigurationFormFields = (
  isTimeAggregationEnabled?: boolean,
  isTargetUrl?: boolean,
  handlePasteAuthorizationToken?: any,
  options?: any,
  optionsLoading?: boolean
) =>
  compact([
    {
      name: SolutionRuleConfigurationField.Name,
      label: 'Name',
      Component: TextField,
      placeholder: 'Enter name',
      required: true,
      validation: Yup.string().required('Name is required'),
      sx: { width: ({ spacing }) => spacing(40) },
    },
    {
      name: SolutionRuleConfigurationField.Enabled,
      label: 'Enable Rule',
      Component: Switch,
      type: 'checkbox',
      required: false,
      labelStyle: { color: '#7B7B7B', opacity: '80%' },
      sx: inputStyle,
      // disabled: isEditMode,
    },
    {
      name: SolutionRuleConfigurationField.SiteIds,
      label: 'Sites',
      required: true,
      validation: Yup.array()
        .nullable()
        .min(1, 'At least 1 Site must be selected')
        .required('Site name is required'),
      options,
      Component: AutoCompleteWithFreeText,
      multiple: true,
      loading: optionsLoading,
      sx: { width: ({ spacing }) => spacing(40), marginTop: '12px' },
    },
    {
      type: 'heading',
      name: 'Conditions',
      label: 'Conditions',
      showInfoIcon: true,
      infoTitle: 'Conditions define when your rule is triggered.',
    },
    {
      name: SolutionRuleConfigurationField.ConditionQueryOperator,
      label: 'Trigger the rule if',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('Trigger the rule if is required'),
      options: DEVICE_GROUP_FILTER_CONDITION_OPTIONS,
      sx: { ...inputStyle, marginBottom: '12px' },
    },
    {
      name: SolutionRuleConfigurationField.TimeAggregation,
      label: 'Time Aggregation',
      Component: Switch,
      type: 'checkbox',
      required: false,
      labelStyle: { color: '#7B7B7B', opacity: '80%' },
      sx: { ...inputStyle, marginBottom: '12px' },
      // disabled: isEditMode,
    },
    isTimeAggregationEnabled && {
      name: SolutionRuleConfigurationField.TimeAggregationValueInMinutes,
      label: 'Time Window',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('Time Window is required'),
      options: RULE_TIME_UNITS,
      sx: { ...inputStyle, marginBottom: '12px' },
    },
    {
      name: SolutionRuleConfigurationField.ConditionQuery,
      appendLabel: 'Filter',
      childFields: getSolutionRuleConfigurationFilterFields(),
      RowComponent: SolutionRuleConfigurationEditFormRow,
      maxRows: 30,
    },
    {
      type: 'heading',
      name: 'Actions',
      label: 'Actions',
    },
    {
      name: SolutionRuleConfigurationField.ActionTypes,
      label: 'Notification',
      Component: AutoCompleteWithFreeText,
      options: RULE_NOTIFICATION_PREFERENCE_OPTIONS,
      multiple: true,
      required: true,
      validation: Yup.array()
        .nullable()
        .min(1, 'At least 1 notification must be selected')
        .required('Notification is required'),
      sx: { width: ({ spacing }) => spacing(40), marginBottom: '12px' },
    },
    isTargetUrl && {
      name: SolutionRuleConfigurationField.TargetUrl,
      label: 'Target URL',
      Component: TextField,
      placeholder: 'https://example.com',
      required: true,
      sx: { width: ({ spacing }) => spacing(80), marginBottom: '12px' },
      validation: Yup.string()
        .required('Target URL is required')
        .url('Target URL should have a correct format'),
    },
    isTargetUrl && {
      name: SolutionRuleConfigurationField.SasToken,
      label: 'SAS Token',
      Component: MaskedInput,
      placeholder: 'Enter SAS Token',
      required: true,
      sx: { width: ({ spacing }) => spacing(80) },
      onClickPasteIcon: handlePasteAuthorizationToken,
      validation: Yup.string()
        .required('SAS Token is required')
        .matches(
          SUBSCRIPTION_SAS_PATTERN,
          'SAS Token should have "SharedAccessSignature sr={sr}&sig={sig}&se={ttl}&skn={saName}" format'
        ),
    },
  ]);

export const getSolutionRuleConfigurationFilterFields = () => [
  {
    name: SolutionRuleConfigurationFilterField.Property,
    label: 'Property Name',
    placeholder: 'Enter the name',
    Component: TextField,
    validation: Yup.string().required('Property name is required'),
    required: true,
    sx: { width: ({ spacing }) => spacing(22) },
  },
  {
    name: SolutionRuleConfigurationFilterField.OperatorValue,
    label: 'Operator',
    placeholder: 'Select the value',
    Component: Select,
    options: SUBSCRIBE_FILTER_OPERATOR_OPTIONS,
    validation: Yup.string().required('Operator is required'),
    required: true,
    sx: { width: ({ spacing }) => spacing(22) },
  },
  {
    name: SolutionRuleConfigurationFilterField.FilterValue,
    label: 'Value',
    placeholder: 'Enter the value',
    Component: TextField,
    validation: Yup.string().required('Value is required'),
    required: true,
    sx: { width: ({ spacing }) => spacing(22) },
  },
];
